import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'

const AuditLogContainer = styled.div`
  background: ${theme.color.background.light};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: inset 0 2px 4px ${theme.color.shadow.light};
`

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin-bottom: 2rem;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px ${theme.color.shadow.light};
`

const TableHeader = styled.thead`
  background: ${theme.color.background.frame};
`

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: ${theme.color.text.base};
  border-bottom: 2px solid ${theme.color.border.light};
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  &:nth-child(even) {
    background: ${theme.color.background.lightGrey};
  }
  
  &:hover {
    background: ${theme.color.background.light};
  }
`

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid ${theme.color.border.light};
  color: ${theme.color.text.base};
  vertical-align: top;
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-size: 1.4rem;
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.color.text.grey};
  font-size: 1.4rem;
  font-style: italic;
`

interface AuditLogEntry {
  user_email: string
  tab_name: string
  action: string
  details: any
  updated_at: string
  admin_user: string
}

const AuditLog: React.FC = () => {
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadAuditLog()
  }, [])

  const loadAuditLog = async () => {
    setIsLoading(true)
    setError('')

    try {
      console.log('AuditLog: Fetching from /api/audit-log')
      const response = await fetch('/api/audit-log')
      
      console.log('AuditLog: Response status:', response.status)
      console.log('AuditLog: Response ok:', response.ok)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('AuditLog: Error response:', errorText)
        throw new Error(`Failed to fetch audit log: ${response.status} ${response.statusText}`)
      }
      
      const data = await response.json()
      console.log('AuditLog: Response data:', data)
      console.log('AuditLog: Number of logs:', data.logs?.length || 0)
      
      setAuditEntries(data.logs || [])
    } catch (err) {
      console.error('Failed to load audit log:', err)
      setError(err instanceof Error ? err.message : 'Failed to load audit log')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDateTime = (isoString: string) => {
    if (!isoString) return 'N/A'
    
    try {
      const date = new Date(isoString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    } catch {
      return 'Invalid Date'
    }
  }

  const formatDetails = (action: string, details: any) => {
    if (!details) return 'No details available'
    
    try {
      const detailsObj = typeof details === 'string' ? JSON.parse(details) : details
      
      switch (action) {
        case 'POINTS_DEPOSITED':
        case 'STARS_DEPOSITED':
          return `${detailsObj.amount || 'Unknown'} points deposited into ${detailsObj.point_source || 'Unknown source'}`
        
        case 'POINTS_SPENT':
          return `${detailsObj.amount || 'Unknown'} points spent from ${detailsObj.point_source || 'Unknown source'}`
        
        case 'POINTS_SUBTRACTED':
          return `${detailsObj.amount || 'Unknown'} points subtracted from ${detailsObj.point_source || 'Unknown source'}`
        
        case 'TIER_PROMOTED':
          return `User tier promoted to ${detailsObj.to_tier || 'Unknown tier'}`
        
        case 'TIER_DEMOTED':
          return `User tier demoted to ${detailsObj.to_tier || 'Unknown tier'}`
        
        case 'OFFER_ISSUED':
        case 'PERK_ISSUED':
          return `Offer issued: ${detailsObj.offer_name || 'Unknown offer'}`
        
        default:
          return JSON.stringify(detailsObj, null, 2)
      }
    } catch {
      return String(details)
    }
  }

  return (
    <AuditLogContainer>
      <Title>Audit Log</Title>
      
      {isLoading ? (
        <LoadingMessage>Loading audit log...</LoadingMessage>
      ) : error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : auditEntries.length === 0 ? (
        <NoDataMessage>No audit entries found</NoDataMessage>
      ) : (
        <Table>
          <TableHeader>
            <tr>
              <TableHeaderCell>User Profile</TableHeaderCell>
              <TableHeaderCell>Changes Made To</TableHeaderCell>
              <TableHeaderCell>Action Taken</TableHeaderCell>
              <TableHeaderCell>Details</TableHeaderCell>
              <TableHeaderCell>Time</TableHeaderCell>
              <TableHeaderCell>Changed Made By</TableHeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {auditEntries.map((entry, index) => (
              <TableRow key={index}>
                <TableCell>{entry.user_email || 'N/A'}</TableCell>
                <TableCell>{entry.tab_name || 'N/A'}</TableCell>
                <TableCell>{entry.action || 'N/A'}</TableCell>
                <TableCell>{formatDetails(entry.action, entry.details)}</TableCell>
                <TableCell>{formatDateTime(entry.updated_at)}</TableCell>
                <TableCell>{entry.admin_user || 'N/A'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </AuditLogContainer>
  )
}

export default AuditLog
